import { useState } from 'react';

import { Button, Flex, Spin, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Card from '@/components/RCard';
import CustomGraphicTooltip from '@/components/RGraphicTooltip';
import RToggleButtons from '@/components/RToggleButtons';
import RTooltip from '@/components/RTooltip';
import useCurrency from '@/contexts/useCurrency';
import { SimulationResultYearlyFinancialAnalysis } from '@/types/simulations';
import { getTickStep, tickFormatter } from '@/utils';

import StyledPlanBatteryBOS from './styles';

const PlantBatteryBOS = () => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const resultId = useParams<Record<string, string>>();
  const {
    data: financialData,
    isLoading,
    error,
  } = useSWR<SimulationResultYearlyFinancialAnalysis>(
    `/simulation/${resultId.resultId}/yearly_financial_analysis/`,
    {
      revalidateOnFocus: false,
    }
  );
  const isDataZero = financialData?.graphData.length === 0;
  const [closedDataKeys, setClosedDataKeys] = useState<string[]>([]);
  const [selectedSwitch, setSelectedSwitch] = useState('Cost');
  const theme = useTheme();

  const toggleClosedDataKeys = (key: string = '') => {
    if (closedDataKeys.includes(key)) {
      setClosedDataKeys(closedDataKeys.filter((item) => item !== key));
    } else {
      setClosedDataKeys([...closedDataKeys, key]);
    }
  };

  const handleSwitchChange = (index: number) => {
    // Index 0: Cost, Index 1: Depreciation
    if (index === 0) {
      setSelectedSwitch('Cost');
    } else if (index === 1) {
      setSelectedSwitch('Depreciation');
    }
  };

  const toggleLabels = [
    t('result.finance.plantBatteryBOS.toggleButtons.cost'),
    t('result.finance.plantBatteryBOS.toggleButtons.depreciation'),
  ];

  const data =
    financialData?.graphData.map((item) => {
      const filteredItem: {
        year: number;
        Plant: number;
        Battery: number;
        BOS: number;
      } = {
        year: item.year,
        Plant: 0,
        Battery: 0,
        BOS: 0,
      };
      if (!closedDataKeys.includes('plant')) {
        filteredItem.Plant =
          selectedSwitch === 'Cost'
            ? item.plantTotalCost
            : item.plantDepreciation;
      }
      if (!closedDataKeys.includes('battery')) {
        filteredItem.Battery =
          selectedSwitch === 'Cost'
            ? item.batteryTotalCost
            : item.batteryDepreciation;
      }
      if (!closedDataKeys.includes('bos')) {
        filteredItem.BOS =
          selectedSwitch === 'Cost' ? item.bosTotalCost : item.bosDepreciation;
      }

      return filteredItem;
    }) || [];

  const allValues = data.map((item) => item.Plant + item.Battery + item.BOS);
  const maxValue = Math.max(...allValues);
  const minValue = Math.min(...allValues);

  const roundToNearest = (num: number, nearest: number) => {
    return Math.round(num / nearest) * nearest;
  };

  const tickStep = getTickStep(
    Math.max(Math.abs(minValue), Math.abs(maxValue))
  );
  const upperBound = roundToNearest(maxValue * 1.3, tickStep);
  const lowerBound = roundToNearest(minValue * 1.3, tickStep);
  const maxAbsBound = Math.max(Math.abs(upperBound), Math.abs(lowerBound));
  const domain =
    selectedSwitch === 'Cost' ? [-maxAbsBound, maxAbsBound] : [0, maxAbsBound];

  const step = selectedSwitch === 'Cost' ? maxAbsBound / 2 : maxAbsBound / 4;
  const ticks =
    selectedSwitch === 'Cost'
      ? [-2 * step, -1 * step, 0, 1 * step, 2 * step]
      : [0, 1 * step, 2 * step, 3 * step, 4 * step]
          .map((value) => roundToNearest(value, tickStep))
          .filter((value) => value !== 0 || maxValue !== 0 || minValue !== 0);

  return (
    <StyledPlanBatteryBOS>
      <Card
        title={
          <Flex gap={8} align="center">
            <Typography.Title level={4} className="fs-17-bold">
              {t('result.finance.plantBatteryBOS.title')}
            </Typography.Title>
            <RTooltip
              title={t('result.finance.plantBatteryBOS.title')}
              description={t('result.finance.plantBatteryBOS.tooltip')}
            >
              <IconInfo />
            </RTooltip>
          </Flex>
        }
        extra={
          <Flex gap={16} align="center">
            <RToggleButtons
              labels={toggleLabels}
              onButtonClick={handleSwitchChange}
            />
            <Button icon={<IconDownload fill={theme.colors.bluePrimary} />} />
          </Flex>
        }
        $paddingBody="24px"
        styles={{ header: { padding: '14px 24px' } }}
        style={{ marginTop: '24px', width: '100%' }}
      >
        {isLoading ? (
          <Flex justify="center" align="center" style={{ height: 400 }}>
            <Spin style={{ margin: '20px' }} />
          </Flex>
        ) : error || isDataZero ? (
          <Flex
            vertical
            gap={12}
            justify="center"
            align="center"
            style={{ height: 400 }}
          >
            <IconError width={50} height={50} fill={theme.colors.tagFailure} />
            {error && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.error.fetchDataError')}
              </Typography.Text>
            )}
            {isDataZero && (
              <Typography.Text className="fs-14-regular text-gray-color text-center">
                {t('result.finance.cashFlow.noDataAvailable')}
              </Typography.Text>
            )}
          </Flex>
        ) : (
          <ResponsiveContainer height={400} width={'100%'}>
            <BarChart data={data}>
              <CartesianGrid vertical={false} horizontal={true} />
              <XAxis
                dataKey="year"
                angle={-45}
                textAnchor="end"
                height={60}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(value) => tickFormatter(value, currencySymbol)}
                domain={domain}
                ticks={ticks.length > 0 ? ticks : undefined}
                tick={{ dx: -4 }}
                axisLine={false}
                tickLine={false}
                label={{
                  angle: -90,
                  position: 'insideLeft',
                }}
              />
              <Tooltip content={<CustomGraphicTooltip />} />
              <Legend
                payload={[
                  {
                    value: t('result.finance.plantBatteryBOS.legend.plant'),
                    id: 'plant',
                    type: 'circle',
                    color: theme.colors.purpleSecondary,
                    inactive: closedDataKeys.includes('plant'),
                  },
                  {
                    value: t('result.finance.plantBatteryBOS.legend.battery'),
                    id: 'battery',
                    type: 'circle',
                    color: theme.colors.orangeSecondary,
                    inactive: closedDataKeys.includes('battery'),
                  },
                  {
                    value: t('result.finance.plantBatteryBOS.legend.bos'),
                    id: 'bos',
                    type: 'circle',
                    color: theme.colors.blueLight,
                    inactive: closedDataKeys.includes('bos'),
                  },
                ]}
                iconSize={10}
                onClick={({ id }) => toggleClosedDataKeys(id)}
              />
              {!closedDataKeys.includes('plant') && (
                <Bar
                  dataKey="Plant"
                  name={t('result.finance.plantBatteryBOS.legend.plant')}
                  fill={theme.colors.purpleSecondary}
                  barSize={16}
                  stackId="a"
                  unit={currencySymbol}
                />
              )}
              {!closedDataKeys.includes('battery') && (
                <Bar
                  dataKey="Battery"
                  name={t('result.finance.plantBatteryBOS.legend.battery')}
                  fill={theme.colors.orangeSecondary}
                  barSize={16}
                  stackId="a"
                  unit={currencySymbol}
                />
              )}
              {!closedDataKeys.includes('bos') && (
                <Bar
                  dataKey="BOS"
                  name={t('result.finance.plantBatteryBOS.legend.bos')}
                  fill={theme.colors.blueLight}
                  barSize={16}
                  stackId="a"
                  unit={currencySymbol}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </StyledPlanBatteryBOS>
  );
};
export default PlantBatteryBOS;
